/**
 * Pure runtime evaluator for form logic rules.
 *
 * Given a tree of LogicNode plus the current form values, decide which
 * actions to apply to which elements. No React, no I/O — easy to unit-test.
 *
 * Ported verbatim from
 * apps/web/src/components/views/DocumentEditorView/logic/evaluate.ts.
 */

import type {
  ConditionOperator,
  LogicCondition,
  LogicGroup,
  LogicNode,
  LogicRule,
  ResolvedElementState,
} from "../types";
import { ANSWER_SENTINEL } from "../elementTypes";

type FormValues = Record<string, unknown>;

function isEmpty(v: unknown): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) {
    if (v.length === 0) return true;
    // Multi-choice's "None" sentinel is an affirmative no-items answer:
    // treat it as empty for empty-checks. Equality / contains operators
    // can still match it explicitly. Sentinel mirrors NONE_VALUE in
    // apps/web/src/components/documentElements/MultiChoice.tsx.
    if (v.length === 1 && v[0] === ANSWER_SENTINEL.none) return true;
    return false;
  }
  if (typeof v === "object") return Object.keys(v as object).length === 0;
  return false;
}

function toNumber(v: unknown): number | null {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function toStr(v: unknown): string {
  if (v === undefined || v === null) return "";
  return String(v);
}

/**
 * Element answers ride a uniform `{ value }` envelope (see
 * `useElementValue`). Multi-choice keeps its own canonical shape
 * `{ selected: string[]; other?: string }` *inside* the envelope.
 * This helper peels both layers when present so comparisons run on
 * the bare answer.
 */
function unwrapValue(v: unknown): unknown {
  // Outer envelope: { value: <inner> } — emitted by useElementValue.
  // Only the top-level `value` key is reserved; never collide with
  // genuine answer shapes by checking that's the *only* key.
  if (
    v &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    "value" in (v as Record<string, unknown>) &&
    Object.keys(v as Record<string, unknown>).length === 1
  ) {
    v = (v as { value: unknown }).value;
  }
  // Inner multi-choice shape: pluck `selected` so the operator code
  // compares against the array of picked names, not [object Object].
  if (
    v &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    Array.isArray((v as { selected?: unknown }).selected)
  ) {
    return (v as { selected: unknown[] }).selected;
  }
  // Single-pick rich-entry shapes that carry a `value` field (radio-
  // cards, single-select dropdown, single-select multi-choice):
  // peel the array so `equals 'X'` / `not_equals 'Y'` operators can
  // compare against the bare string the designer wrote. Entity-
  // picker rich entries use `id` (not `value`) so they don't trip
  // this case — they pass through as objects.
  if (
    Array.isArray(v) &&
    v.length === 1 &&
    v[0] !== null &&
    typeof v[0] === "object" &&
    "value" in (v[0] as Record<string, unknown>)
  ) {
    return (v[0] as { value: unknown }).value;
  }
  return v;
}

function applyOperator(
  operator: ConditionOperator,
  rawActual: unknown,
  expected: unknown,
): boolean {
  const actual = unwrapValue(rawActual);
  switch (operator) {
    case "equals":
      // Array actual (multi-choice or multi-select): treat as a match when
      // the array contains the expected value AND nothing else. Single
      // primitives keep the old stringify-equality behaviour.
      if (Array.isArray(actual)) {
        return actual.length === 1 && toStr(actual[0]) === toStr(expected);
      }
      return toStr(actual) === toStr(expected);
    case "not_equals":
      if (Array.isArray(actual)) {
        return !(actual.length === 1 && toStr(actual[0]) === toStr(expected));
      }
      return toStr(actual) !== toStr(expected);
    case "contains":
      if (Array.isArray(actual))
        return actual.map(toStr).includes(toStr(expected));
      return toStr(actual).includes(toStr(expected));
    case "not_contains":
      if (Array.isArray(actual))
        return !actual.map(toStr).includes(toStr(expected));
      return !toStr(actual).includes(toStr(expected));
    case "starts_with":
      return toStr(actual).startsWith(toStr(expected));
    case "ends_with":
      return toStr(actual).endsWith(toStr(expected));
    case "greater_than": {
      const a = toNumber(actual);
      const b = toNumber(expected);
      return a !== null && b !== null && a > b;
    }
    case "less_than": {
      const a = toNumber(actual);
      const b = toNumber(expected);
      return a !== null && b !== null && a < b;
    }
    case "greater_or_equal": {
      const a = toNumber(actual);
      const b = toNumber(expected);
      return a !== null && b !== null && a >= b;
    }
    case "less_or_equal": {
      const a = toNumber(actual);
      const b = toNumber(expected);
      return a !== null && b !== null && a <= b;
    }
    case "is_empty":
      return isEmpty(actual);
    case "is_not_empty":
      return !isEmpty(actual);
    case "is_checked":
      return actual === true;
    case "is_not_checked":
      return actual !== true;
    default:
      return false;
  }
}

function evaluateCondition(c: LogicCondition, values: FormValues): boolean {
  if (!c.elementId) return false;
  return applyOperator(c.operator, values[c.elementId], c.value);
}

export function evaluateLogicNode(
  node: LogicNode,
  values: FormValues,
): boolean {
  if (node.kind === "condition") return evaluateCondition(node, values);
  // group
  if (node.children.length === 0) {
    // Vacuous: AND on empty = true, OR on empty = false (matches spec)
    return node.operator === "AND";
  }
  return node.operator === "AND"
    ? node.children.every((c) => evaluateLogicNode(c, values))
    : node.children.some((c) => evaluateLogicNode(c, values));
}

export function evaluateRule(rule: LogicRule, values: FormValues): boolean {
  if (!rule.enabled) return false;
  return evaluateLogicNode(rule.root, values);
}

/**
 * Walk every rule, evaluate it against the form's current values, and reduce
 * each rule's actions into a per-target ResolvedElementState. Later rules
 * override earlier ones for the same field.
 */
export function resolveLogic(
  rules: LogicRule[] | undefined,
  values: FormValues,
): Map<string, ResolvedElementState> {
  const out = new Map<string, ResolvedElementState>();
  if (!rules || rules.length === 0) return out;

  for (const rule of rules) {
    if (!rule.enabled) continue;
    const matched = evaluateLogicNode(rule.root, values);
    if (!matched) continue;

    for (const action of rule.actions) {
      if (!action.targetElementId) continue;
      const prev = out.get(action.targetElementId) || {};
      switch (action.type) {
        case "show":
          prev.hidden = false;
          break;
        case "hide":
          prev.hidden = true;
          break;
        case "require":
          prev.required = true;
          break;
        case "unrequire":
          prev.required = false;
          break;
        case "enable":
          prev.disabled = false;
          break;
        case "disable":
          prev.disabled = true;
          break;
        case "set_value":
          prev.setValue = action.value;
          break;
        case "calculate":
          // Formula evaluation deferred; pass through as-is for now.
          prev.calculated = action.value;
          break;
      }
      out.set(action.targetElementId, prev);
    }
  }

  return out;
}

/**
 * Recursive validity helper used by the rule editor's save gate.
 * A group is valid if it has ≥ 1 child and every leaf condition has an elementId.
 */
export function isLogicGroupValid(group: LogicGroup): boolean {
  if (group.children.length === 0) return false;
  return group.children.every((child) =>
    child.kind === "group" ? isLogicGroupValid(child) : child.elementId !== "",
  );
}

/**
 * A rule is "simple" if its root has no nested groups — only condition
 * children. Used by the editor to decide whether the Simple/Advanced toggle
 * can offer Simple mode.
 */
export function isSimpleRule(rule: LogicRule): boolean {
  return rule.root.children.every((c) => c.kind === "condition");
}
